<template>
  <section class="google-stage">
    <div class="google-stage__hero-bg">
      <CdnImage
        alt="Ein Profilbild einer Person, die die Google Pixel Buds trägt"
        src="/content/content-images/landingpages/google/stage/4831ee97ef534e59bc84979900ef82de/keyvisual-buehne.png"
        src-sm="/content/content-images/landingpages/google/stage/fa2e0152ead8320e662fed368b38a36d/keyvisual-buehne-cut.png"
        sizes="xs:50vw sm:50vw lg:50vw"
      />
    </div>
    <div class="google-stage__header">
      <Heading
        tag="h1"
        align="center"
      >
        <CdnImage
          ref="heroTitleImage"
          :alt="data.titleAlt"
          :src="data.titleSrc"
          class="google-stage__header__image"
          render-without-figure
        />
        <span hidden>{{ data.titleAlt }}</span>
      </Heading>
      <Heading
        :text="data.mainTitle"
        class="google-stage__header__title"
        font-weight="ultra"
        :size="$screenSize.isAtLeastDesktopScreen.value ? 'xl' : 'lg'"
        tag="h2"
        text-uppercase
        spaces-bottom
        align="center"
        style="white-space: pre"
      />
    </div>
    <div class="google-stage__products">
      <div
        v-for="item in data.products"
        ref="productRefs"
        :key="item.ref"
        :class="`google-stage__products__item animated--${item.ref}`"
        @click="$emit('go-to-section', item.ref)"
      >
        <CdnImage
          :alt="item.alt"
          class="google-stage__products__item__image"
          :src="item.src"
          :style="item.imageStyle"
          render-without-figure
        />
        <div class="google-stage__products__item__title">
          <Heading
            :text="item.alt"
            align="center"
            text-uppercase
            size="sm"
            font-weight="ultra"
            tag="span"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ScrollAnimation from '@i22/scroll-animation';
import { Heading } from '@i22/td-longtail-vue-components';
import CdnImage from '~/components/cdn-image.vue';

export default {
  name: 'GoogleStage',
  components: {
    Heading,
    CdnImage,
  },
  mixins: [ScrollAnimation],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  emits: ['go-to-section'],
  mounted() {
    this.startAnimation();
  },
  methods: {
    startAnimation() {
      const yDesktop = ['55%', '40%', '30%'];
      const yMobile = '45%';

      ['pixelBuds', 'pixelStand', 'pixelCases'].forEach((ref, index) => {
        this.createMediaScrollTrigger({
          '(max-width: 640px)': () => {
            this.createScrollTrigger({
              trigger: this.$refs.productRefs[index],
              start: 'top top+=700px',
              end: 'bottom center',
              scrub: index,
              ease: 'expo.inOut',
              animation: this.createTweenFrom(`.animated--${ref}`, {
                y: yMobile,
              }),
            });
          },
          '(min-width: 641px)': () => {
            this.createScrollTrigger({
              trigger: this.$refs.productRefs[index],
              start: 'top-=50 center',
              end: 'bottom-=250 top+=300',
              scrub: index + 2,
              ease: 'expo.inOut',
              animation: this.createTweenFrom(`.animated--${ref}`, {
                y: yDesktop[index],
              }),
            });
          },
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.google-stage {
  position: relative;
  background: #ffddd0;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 300px 1fr;

  @include mq($mq-sm) {
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }

  &__header {
    grid-area: 1 / 1 / 2 / 2;
    margin-top: 2rem;
    z-index: 1;

    &__title2 {
      @include mq($mq-sm) {
      }
    }

    @include mq($mq-sm) {
      margin-top: unset;
      grid-area: 2 / 3 / 4 / 6;
    }
    @include mq($mq-md) {
      margin-top: unset;
      grid-area: 2 / 3 / 4 / 6;
    }
    @include mq($mq-xl) {
      grid-area: 2 / 3 / 4 / 5;
    }

    &__image {
      width: 150px;
      aspect-ratio: 608 / 261;
      margin: auto;
      margin-bottom: 2rem;
    }
  }

  &__products {
    grid-area: 2 / 1 / 4 / 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $grid-base;
    transform: translateY(2rem);
    z-index: 2;
    margin-bottom: 3rem;
    cursor: pointer;

    @include mq($mq-sm) {
      gap: unset;
      flex-direction: row;
      justify-content: flex-end;
      transform: translateY(4rem);
      grid-area: 4 / 2 / 5 / 7;
      align-items: initial;
    }

    @include mq($mq-md) {
      transform: translateY(5rem);
      grid-area: 4 / 3 / 5 / 7;
    }

    @include mq($mq-xl) {
      grid-area: 4 / 4 / 5 / 7;
    }

    &__item {
      position: relative;
      margin: auto;

      @include mq(false, $mq-sm - 1) {
        width: 50%;
        display: flex;
        justify-content: center;
      }

      @include mq($mq-sm) {
        margin: unset;
        transform: translateX(0);
      }
      &__title {
        position: absolute;
        bottom: 0.7rem;
        left: 0;
        right: 0;
      }
    }
  }

  &__hero-bg {
    z-index: 0;
    grid-area: 2 / 1 / 4 / 2;
    overflow: hidden;

    :deep(img) {
      width: 100%;
      aspect-ratio: 128 / 229;
    }

    @include mq($mq-sm) {
      grid-area: 1 / 1 / 5 / 4;
      display: flex;
      justify-content: flex-start;
      overflow: visible;
      margin-left: -280px;

      :deep(img) {
        aspect-ratio: 256 / 243;
        height: 100%;
        width: auto;
        max-width: none;
      }
    }

    @include mq($mq-lg) {
      grid-area: 1 / 1 / 5 / 4;
      margin-left: -220px;
    }
    @include mq($mq-xl) {
      grid-area: 1 / 1 / 5 / 3;
    }
  }
}
</style>
